import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Custom small markers so we don't need the default icon PNG asset
const pinIcon = (color) =>
  L.divIcon({
    className: "nemt-pin",
    html: `<span style="display:block;width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.35)"></span>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
  });

const driverIcon = L.divIcon({
  className: "nemt-driver",
  html: `<span style="display:block;width:16px;height:16px;border-radius:50%;background:#0EA5E9;border:3px solid #fff;box-shadow:0 0 0 4px rgba(14,165,233,0.35);animation:pulse 1.5s infinite"></span>
         <style>@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(14,165,233,0.5)}70%{box-shadow:0 0 0 10px rgba(14,165,233,0)}100%{box-shadow:0 0 0 0 rgba(14,165,233,0)}}</style>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const trailIcon = L.divIcon({
  className: "nemt-trail",
  html: `<span style="display:block;width:6px;height:6px;border-radius:50%;background:#6366F1;border:2px solid #fff"></span>`,
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

export default function LeafletMap({ pickup, dropoff, route, driver, trail, drivers, onDriverClick, height = 380 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: true }).setView([40.75, -73.99], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layers = layersRef.current;

    // Remove old
    Object.values(layers).forEach((l) => {
      if (Array.isArray(l)) l.forEach((x) => map.removeLayer(x));
      else if (l) map.removeLayer(l);
    });
    layersRef.current = {};

    const bounds = [];
    if (pickup) {
      layersRef.current.pickup = L.marker([pickup.lat, pickup.lng], { icon: pinIcon("#10B981") })
        .addTo(map).bindTooltip("Pickup", { permanent: false });
      bounds.push([pickup.lat, pickup.lng]);
    }
    if (dropoff) {
      layersRef.current.dropoff = L.marker([dropoff.lat, dropoff.lng], { icon: pinIcon("#F43F5E") })
        .addTo(map).bindTooltip("Dropoff", { permanent: false });
      bounds.push([dropoff.lat, dropoff.lng]);
    }
    if (route && route.length) {
      layersRef.current.route = L.polyline(route, { color: "#005E82", weight: 4, opacity: 0.75 }).addTo(map);
      bounds.push(...route);
    }
    if (trail && trail.length) {
      layersRef.current.trail = L.polyline(trail, { color: "#6366F1", weight: 3, opacity: 0.9, dashArray: "6,4" }).addTo(map);
      bounds.push(...trail);
    }
    if (driver) {
      layersRef.current.driver = L.marker([driver.lat, driver.lng], { icon: driverIcon, zIndexOffset: 500 }).addTo(map);
      bounds.push([driver.lat, driver.lng]);
    }
    if (drivers && drivers.length) {
      const arr = [];
      drivers.forEach((d) => {
        const m = L.marker([d.lat, d.lng], { icon: driverIcon, zIndexOffset: 500 }).addTo(map);
        m.bindTooltip(d.label || "Driver", { permanent: false, direction: "top" });
        if (onDriverClick) m.on("click", () => onDriverClick(d));
        arr.push(m);
        bounds.push([d.lat, d.lng]);
      });
      layersRef.current.drivers = arr;
    }
    if (bounds.length) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [pickup, dropoff, route, driver, trail, drivers, onDriverClick]);

  return <div ref={containerRef} style={{ height, width: "100%" }} data-testid="leaflet-map" />;
}
